import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Select, Tag, Tooltip } from "antd";
import { X, Edit2 } from "react-feather";

import styles from "./stylesSortingTableRow.module.css";
import ToggleSwitch from "../../../../CommonComponents/ToggleSwitch/ToggleSwitch";
import ClipPath from "../../../../../assets/icons/ic_clip-path.svg";
import { AppStrings } from "../../../../../utils/constants/AppStrings";

// Map item types to display labels and colors
const TYPE_CONFIG = {
  SPORT: { label: "Sport", color: "var(--grey-700)" },
  LEAGUE: { label: "League", color: "var(--grey-700)" },
  EVENT: { label: "Match", color: "var(--grey-700)" },
  MATCH: { label: "Match", color: "var(--grey-700)" },
};

const SortingTableRow = ({
  item,
  order,
  toggleStatus,
  removeItem,
  onRename,
  canEdit = true,
  enablePositionSelect = false,
  positionCount = 0,
  onPositionChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "transform 0.2s ease, box-shadow 0.2s ease",
  };

  const typeConfig = TYPE_CONFIG[item.type] || {
    label: item.type,
    color: "#6B7280",
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={styles.categoryRow}
    >
      {/* Drag Handle */}
      <td className={styles.dragCell} {...(canEdit ? listeners : {})}>
        <img
          className={styles.dragHandle}
          src={ClipPath}
          alt="Drag"
          style={{
            cursor: canEdit ? "grab" : "not-allowed",
            opacity: canEdit ? 1 : 0.5,
          }}
        />
      </td>

      {/* Order Number */}
      <td className={styles.orderNumberCell}>
        <span className={styles.orderNumber}>{order}</span>
      </td>

      {/* Name */}
      <td className={styles.leagueCell}>
        <div className={styles.leagueInfo}>
          <span className={styles.leagueName}>{item.ename}</span>
        </div>
      </td>

      {/* Type */}
      {item.type !== undefined && (
        <td className={styles.typeCell}>
          <Tag
            className={styles.typeTag}
            style={{
              backgroundColor: typeConfig.color,
              color: "#fff",
              border: "none",
            }}
          >
            {typeConfig.label}
          </Tag>
        </td>
      )}

      {/* Status Toggle */}
      {item.is_active !== undefined && (
        <td className={styles.statusCell}>
          <ToggleSwitch
            status={item.is_active}
            eventCode={1}
            updateEventCode={toggleStatus}
            data={item}
            onlyUpdate={true}
            disabled={!canEdit}
          />
        </td>
      )}

      {/* Market IDs */}
      {item.market_ids !== undefined && (
        <td className={styles.marketIdsCell}>
          <div className={styles.marketIdsInfo}>
            <span className={styles.marketIds}>
              {item.market_ids.join(", ")}
            </span>
          </div>
        </td>
      )}

      {/* Action - Edit and Delete */}
      {(removeItem || onRename) && (
        <td className={styles.actionCell}>
          <div className={styles.actionButtons}>
            {onRename && (
              <Tooltip title={AppStrings.RENAME}>
                <Button
                  type="text"
                  icon={<Edit2 size={12} />}
                  onClick={() => onRename(item)}
                  className={styles.editButton}
                  disabled={!canEdit}
                />
              </Tooltip>
            )}
            {removeItem && (
              <Tooltip title={AppStrings.DELETE}>
                <Button
                  type="text"
                  icon={<X size={12} />}
                  onClick={() => removeItem(item)}
                  className={styles.deleteButton}
                  disabled={!canEdit}
                />
              </Tooltip>
            )}
          </div>
        </td>
      )}

      {enablePositionSelect && (
        <td
          className={styles.positionSelectCell}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Select
            aria-label={AppStrings.ORDER}
            className={styles.positionSelect}
            options={Array.from({ length: positionCount }, (_, index) => ({
              label: `${index + 1}`,
              value: index + 1,
            }))}
            value={order}
            onChange={onPositionChange}
            disabled={!canEdit}
            showSearch
            filterOption={(input, option) => option.label.startsWith(input)}
          />
        </td>
      )}
    </tr>
  );
};

export default SortingTableRow;
